import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Things.module.scss';
import { useProductsStore } from '../../store/productsStore';
import proxyUrl from '../../utils/proxyUrl';

export function getDriveImageUrls(raw) {
  if (!raw) return [];
  return [raw.split(',')[0].trim()];
}

function SmartImage({ urls = [], alt, className }) {
  const [i, setI] = useState(0);
  const [useProxy, setUseProxy] = useState(false);
  const rawSrc = urls[i] || '';
  const src = !rawSrc ? '/placeholder.jpg' : useProxy ? proxyUrl(rawSrc) : rawSrc;

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => {
        if (!useProxy && rawSrc && !rawSrc.startsWith('/')) {
          setUseProxy(true);
        } else if (i < urls.length - 1) {
          setUseProxy(false);
          setI(i + 1);
        }
      }}
    />
  );
}

function Things() {
  const { products, fetch, loading, error } = useProductsStore();
  const navigate = useNavigate();

  const [selectedFilter, setSelectedFilter] = useState('Все');
  const [selectedSubFilter, setSelectedSubFilter] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { fetch(); }, [fetch]);

  const filterTree = useMemo(() => {
    const tree = {};
    products.forEach(({ 'Фильтр': f, 'ДопФильтр': sf }) => {
      const filter = f?.trim() || 'Без категории';
      const sub = sf?.trim() || null;
      if (!tree[filter]) tree[filter] = new Set();
      if (sub) tree[filter].add(sub);
    });
    return tree;
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (selectedFilter === 'Все') return products;
    return products.filter((p) => {
      const f = p['Фильтр']?.trim();
      const sf = p['ДопФильтр']?.trim();
      if (f !== selectedFilter) return false;
      if (selectedSubFilter) return sf === selectedSubFilter;
      return true;
    });
  }, [products, selectedFilter, selectedSubFilter]);

  const itemsPerPage = window.innerWidth > 986 ? 9 : 8;
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleProductClick = (product) => {
    navigate(`/product/${encodeURIComponent(product['Товар'])}`);
  };

  const renderFilters = () => (
    <ul>
      <li
        className={selectedFilter === 'Все' ? styles.activeFilter : ''}
        onClick={() => { setSelectedFilter('Все'); setSelectedSubFilter(null); setCurrentPage(1); }}
      >
        Все товары
      </li>
      {Object.entries(filterTree).map(([filterName, subfiltersSet]) => {
        const subfilters = Array.from(subfiltersSet);
        const isActiveFilter = selectedFilter === filterName;
        return (
          <li key={filterName}>
            <div
              className={isActiveFilter ? styles.activeFilter : ''}
              onClick={() => { setSelectedFilter(filterName); setSelectedSubFilter(null); setCurrentPage(1); }}
            >
              {filterName}
            </div>
            {isActiveFilter && subfilters.length > 0 && (
              <ul className={styles.subFilterList}>
                {subfilters.map((sf) => (
                  <li
                    key={sf}
                    className={selectedSubFilter === sf ? styles.activeSubFilter : ''}
                    onClick={() => { setSelectedSubFilter(sf); setCurrentPage(1); }}
                  >
                    {sf}
                  </li>
                ))}
                <li
                  onClick={() => { setSelectedSubFilter(null); setCurrentPage(1); }}
                  style={{ fontStyle: 'italic' }}
                >
                  Все {filterName.toLowerCase()}
                </li>
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );

  if (loading) return <div>Загрузка...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className={styles.things}>
      <h1 className={styles.thingsTitle}>ТОВАРЫ</h1>

      <button className={styles.filterBtn} onClick={() => setIsModalOpen(true)}>
        Фильтры
      </button>

      <div className={styles.thingsLayout}>
        <aside className={styles.thingsSidebar}>
          {renderFilters()}
        </aside>

        <div className={styles.thingsContainer}>
          {paginatedProducts.map((product, i) => {
            const inStock = product['Наличие']?.toLowerCase() === 'да';
            return (
              <div
                key={i}
                className={styles.thingsContainerBlock}
                onClick={() => handleProductClick(product)}
              >
               <div className={styles.productImageWrapper}>
                  <SmartImage
                    urls={getDriveImageUrls(product['Фото'])}
                    alt={product['Товар']}
                    className={styles.productImage}
                  />
                </div>
                <div className={styles.thingsContainerBlockInside}>
                  <p className={styles.thingsContainerBlockInsidePrice}>{product['Цена']} ₽</p>
                  <p className={styles.thingsContainerBlockInsideTitle}>{product['Товар']}</p>
                  <p className={`${styles.thingsContainerBlockInsideSubtitle} ${inStock ? styles.inStock : styles.outOfStock}`}>
                    {inStock ? 'В наличии' : 'Нет в наличии'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.pagination}>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            className={`${styles.pageBtn} ${currentPage === i + 1 ? styles.activePage : ''}`}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            <h2>Фильтры</h2>
            {renderFilters()}
          </div>
        </div>
      )}
    </div>
  );
}

export default Things;
